import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, onSnapshot, doc, addDoc, deleteDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

const useToast = () => ({ success: console.log, error: console.log });

export default function VendorFinanceTab({ user, db, storage, functions, isUserAuthenticated, bookings }) {
    const [activeSubTab, setActiveSubTab] = useState('payouts');

    return (
        <div className="space-y-6">
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                <button onClick={() => setActiveSubTab('payouts')} className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeSubTab === 'payouts' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Payouts (Stripe)</button>
                <button onClick={() => setActiveSubTab('expenses')} className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeSubTab === 'expenses' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Expenses & Profit</button>
                <button onClick={() => setActiveSubTab('tax')} className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeSubTab === 'tax' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Tax Reports</button>
            </div>

            {activeSubTab === 'payouts' ? (
                <VendorPayouts user={user} functions={functions} isUserAuthenticated={isUserAuthenticated} bookings={bookings} />
            ) : activeSubTab === 'expenses' ? (
                <VendorExpenses user={user} db={db} storage={storage} bookings={bookings} />
            ) : (
                <VendorTaxReports user={user} db={db} bookings={bookings} />
            )}
        </div>
    );
}

function VendorTaxReports({ user, db, bookings }) {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    
    // Filter relevant bookings (Completed/Confirmed only)
    const taxBookings = useMemo(() => {
        return bookings.filter(b => {
            // Exclude cancelled/declined/pending. Include completed or active bookings where payment is likely secured.
            if (['cancelled', 'declined', 'pending'].includes(b.status)) return false;
            
            // Use event date for tax reporting period (Accrual basis approximation)
            const d = new Date(b.date);
            return d.getFullYear() === parseInt(selectedYear);
        });
    }, [bookings, selectedYear]);

    const reportData = useMemo(() => {
        const monthlyData = Array(12).fill(0).map((_, i) => ({
            month: new Date(0, i).toLocaleString('default', { month: 'long' }),
            grossSales: 0,
            taxableSales: 0,
            taxCollected: 0,
            bookingsCount: 0
        }));

        taxBookings.forEach(b => {
            const month = new Date(b.date).getMonth();
            const tax = parseFloat(b.taxAmount || 0);
            const total = parseFloat(b.totalCost || 0);
            
            // Estimate taxable sales: Total - Tax Collected. 
            // Note: This assumes the total includes tax. (Total = Subtotal + Fees + Tax)
            const taxable = total - tax; 

            monthlyData[month].grossSales += total;
            monthlyData[month].taxableSales += taxable;
            monthlyData[month].taxCollected += tax;
            monthlyData[month].bookingsCount += 1;
        });

        return monthlyData;
    }, [taxBookings]);

    const totals = reportData.reduce((acc, curr) => ({
        grossSales: acc.grossSales + curr.grossSales,
        taxableSales: acc.taxableSales + curr.taxableSales,
        taxCollected: acc.taxCollected + curr.taxCollected
    }), { grossSales: 0, taxableSales: 0, taxCollected: 0 });

    const exportCSV = () => {
        const headers = ["Month", "Bookings", "Gross Sales", "Taxable Sales", "Tax Collected"];
        const rows = reportData.map(d => [
            d.month,
            d.bookingsCount,
            d.grossSales.toFixed(2),
            d.taxableSales.toFixed(2),
            d.taxCollected.toFixed(2)
        ]);
        
        // Add totals row
        rows.push(["TOTAL", taxBookings.length, totals.grossSales.toFixed(2), totals.taxableSales.toFixed(2), totals.taxCollected.toFixed(2)]);

        const csvContent = [
            headers.join(','),
            ...rows.map(r => r.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `Tax_Report_${selectedYear}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800">Sales Tax Liability Report</h3>
                <div className="flex gap-2">
                    <select 
                        value={selectedYear} 
                        onChange={(e) => setSelectedYear(e.target.value)} 
                        className="p-2 border rounded-md text-sm bg-gray-50 focus:ring-pink-500"
                    >
                        {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <button onClick={exportCSV} className="flex items-center gap-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-md font-semibold text-sm hover:bg-blue-100 transition">
                        <span>⬇️</span> Download CSV
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Gross Sales</p>
                    <p className="text-2xl font-extrabold text-gray-900 mt-1">${totals.grossSales.toFixed(2)}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Taxable Sales (Est)</p>
                    <p className="text-2xl font-extrabold text-gray-900 mt-1">${totals.taxableSales.toFixed(2)}</p>
                </div>
                <div className="bg-gradient-to-br from-pink-50 to-white p-6 rounded-lg shadow-sm border border-pink-100">
                    <p className="text-xs text-pink-700 font-bold uppercase tracking-wider">Total Tax Collected</p>
                    <p className="text-2xl font-extrabold text-pink-700 mt-1">${totals.taxCollected.toFixed(2)}</p>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-100">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-500 font-semibold border-b">
                            <tr>
                                <th className="p-4">Month</th>
                                <th className="p-4 text-center">Bookings</th>
                                <th className="p-4 text-right">Gross Sales</th>
                                <th className="p-4 text-right">Taxable Sales</th>
                                <th className="p-4 text-right">Tax Collected</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {reportData.map((row, idx) => (
                                <tr key={idx} className="hover:bg-gray-50 transition">
                                    <td className="p-4 font-medium text-gray-800">{row.month}</td>
                                    <td className="p-4 text-center text-gray-600">{row.bookingsCount}</td>
                                    <td className="p-4 text-right text-gray-600">${row.grossSales.toFixed(2)}</td>
                                    <td className="p-4 text-right text-gray-600">${row.taxableSales.toFixed(2)}</td>
                                    <td className="p-4 text-right font-bold text-gray-800">${row.taxCollected.toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-gray-50 font-bold text-gray-900 border-t-2 border-gray-200">
                            <tr>
                                <td className="p-4">TOTAL</td>
                                <td className="p-4 text-center">{taxBookings.length}</td>
                                <td className="p-4 text-right">${totals.grossSales.toFixed(2)}</td>
                                <td className="p-4 text-right">${totals.taxableSales.toFixed(2)}</td>
                                <td className="p-4 text-right">${totals.taxCollected.toFixed(2)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
            
            <p className="text-xs text-gray-400 italic text-center">
                * Disclaimer: This report is generated based on your transaction history and collected tax amounts. It is for informational purposes only and does not constitute official tax advice or filings. Please consult a qualified tax professional for your business tax obligations.
            </p>
        </div>
    );
}

function VendorExpenses({ user, db, storage, bookings }) {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newExpense, setNewExpense] = useState({ description: '', amount: '', category: 'Inventory', date: new Date().toISOString().split('T')[0] });
    const [receiptFile, setReceiptFile] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
    const toast = useToast();
    
    useEffect(() => {
        const q = query(collection(db, `users/${user.uid}/expenses`));
        const unsub = onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => ({id: d.id, ...d.data()}));
            data.sort((a, b) => new Date(b.date) - new Date(a.date));
            setExpenses(data);
            setLoading(false);
        });
        return () => unsub();
    }, [user, db]);

    const handleAdd = async () => {
        if(!newExpense.description || !newExpense.amount) return;
        setIsSaving(true);
        try {
            let receiptUrl = null;
            
            if (receiptFile && storage) {
                const fileExt = receiptFile.name.split('.').pop();
                const filePath = `users/${user.uid}/receipts/${Date.now()}_receipt.${fileExt}`;
                const fileRef = ref(storage, filePath);
                await uploadBytes(fileRef, receiptFile);
                receiptUrl = await getDownloadURL(fileRef);
            }

            await addDoc(collection(db, `users/${user.uid}/expenses`), {
                ...newExpense,
                amount: parseFloat(newExpense.amount),
                receiptUrl,
                createdAt: serverTimestamp()
            });
            
            setNewExpense({ description: '', amount: '', category: 'Inventory', date: new Date().toISOString().split('T')[0] });
            setReceiptFile(null);
            toast.success('Expense logged successfully!');
        } catch (error) {
            console.error("Error adding expense:", error);
            toast.error('Failed to log expense.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if(!window.confirm("Delete this expense record?")) return;
        await deleteDoc(doc(db, `users/${user.uid}/expenses`, id));
    };

    const exportData = (data, filename) => {
        if (!data || data.length === 0) return alert("No data to export.");
        
        const flatten = (obj) => {
            const result = {};
            for (const key in obj) {
                if (typeof obj[key] === 'object' && obj[key] !== null && !obj[key].seconds) {
                    result[key] = JSON.stringify(obj[key]);
                } else if (obj[key] && obj[key].seconds) {
                    result[key] = new Date(obj[key].seconds * 1000).toLocaleDateString();
                } else {
                    result[key] = obj[key];
                }
            }
            return result;
        };

        const flatData = data.map(flatten);
        const headersSet = new Set();
        flatData.forEach(row => Object.keys(row).forEach(k => headersSet.add(k)));
        const headers = Array.from(headersSet);
        
        const csvContent = [
            headers.join(','),
            ...flatData.map(row => headers.map(fieldName => {
                let val = row[fieldName];
                if (val === undefined || val === null) val = '';
                if (typeof val === 'string') val = `"${val.replace(/"/g, '""')}"`;
                return val;
            }).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const filteredBookings = bookings.filter(b => {
        if (b.status === 'cancelled' || b.status === 'declined') return false;
        if (filterYear === 'all') return true;
        return new Date(b.date).getFullYear().toString() === filterYear;
    });

    const filteredExpenses = expenses.filter(e => {
        if (filterYear === 'all') return true;
        return new Date(e.date).getFullYear().toString() === filterYear;
    });

    const totalRevenue = filteredBookings.reduce((acc, b) => acc + (b.totalCost || 0) - (b.renterFee || 0), 0);
    const totalExpenses = filteredExpenses.reduce((acc, e) => acc + (e.amount || 0), 0);
    const netProfit = totalRevenue - totalExpenses;

    const expensesByCategory = filteredExpenses.reduce((acc, e) => {
        acc[e.category] = (acc[e.category] || 0) + e.amount;
        return acc;
    }, {});

    if (loading) return <div className="p-8 text-center">Loading financials...</div>;

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Filter & Export Bar */}
            <div className="flex flex-wrap gap-4 justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-gray-700 uppercase tracking-wide">Financial Year:</span>
                    <select 
                        value={filterYear} 
                        onChange={e => setFilterYear(e.target.value)} 
                        className="p-2 border border-gray-300 rounded-lg text-sm font-bold text-pink-700 bg-pink-50 focus:ring-2 focus:ring-pink-500 outline-none cursor-pointer shadow-sm"
                    >
                        <option value="all">All Time</option>
                        {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y.toString()}>{y}</option>)}
                    </select>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => exportData(filteredBookings, `bookings_${filterYear}.csv`)} className="text-sm flex items-center gap-2 text-gray-600 hover:text-pink-600 font-semibold bg-white border border-gray-200 px-3 py-2 rounded-lg shadow-sm transition">
                        <span>⬇️</span> Export Bookings
                    </button>
                    <button onClick={() => exportData(filteredExpenses, `expenses_${filterYear}.csv`)} className="text-sm flex items-center gap-2 text-gray-600 hover:text-pink-600 font-semibold bg-white border border-gray-200 px-3 py-2 rounded-lg shadow-sm transition">
                        <span>⬇️</span> Export Expenses
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center transition-all duration-300">
                    <div className="bg-pink-50 text-pink-500 rounded-full h-14 w-14 flex items-center justify-center text-2xl flex-shrink-0">💰</div>
                    <div className="ml-5 min-w-0">
                        <p className="text-xs text-gray-500 font-bold uppercase tracking-wide mb-1 truncate">Gross Revenue</p>
                        <p className="text-2xl font-extrabold text-gray-900 truncate" title={`$${totalRevenue.toFixed(2)}`}>${totalRevenue.toFixed(2)}</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center transition-all duration-300">
                    <div className="bg-pink-50 text-pink-500 rounded-full h-14 w-14 flex items-center justify-center text-2xl flex-shrink-0">💸</div>
                    <div className="ml-5 min-w-0">
                        <p className="text-xs text-gray-500 font-bold uppercase tracking-wide mb-1 truncate">Total Expenses</p>
                        <p className="text-2xl font-extrabold text-gray-900 truncate" title={`$${totalExpenses.toFixed(2)}`}>${totalExpenses.toFixed(2)}</p>
                    </div>
                </div>
                <div className={`bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center transition-all duration-300`}>
                    <div className={`rounded-full h-14 w-14 flex items-center justify-center text-2xl flex-shrink-0 ${netProfit >= 0 ? 'bg-green-50 text-green-500' : 'bg-red-50 text-red-500'}`}>
                        {netProfit >= 0 ? '📈' : '📉'}
                    </div>
                    <div className="ml-5 min-w-0">
                        <p className="text-xs text-gray-500 font-bold uppercase tracking-wide mb-1 truncate">Net Profit</p>
                        <p className={`text-2xl font-extrabold truncate ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`} title={`$${netProfit.toFixed(2)}`}>${netProfit.toFixed(2)}</p>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Form & Charts */}
                <div className="lg:col-span-1 space-y-6">
                    {/* Add Expense Form */}
                    <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                        <h3 className="text-lg font-bold mb-4 text-gray-800 border-b pb-2">Log New Expense</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Date</label>
                                <input type="date" value={newExpense.date} onChange={e => setNewExpense({...newExpense, date: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none text-sm" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Description</label>
                                <input type="text" placeholder="e.g. New Tents, Gas" value={newExpense.description} onChange={e => setNewExpense({...newExpense, description: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none text-sm" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Amount ($)</label>
                                    <input type="number" placeholder="0.00" value={newExpense.amount} onChange={e => setNewExpense({...newExpense, amount: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none text-sm" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Category</label>
                                    <select value={newExpense.category} onChange={e => setNewExpense({...newExpense, category: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-pink-500 outline-none text-sm">
                                        <option>Inventory</option>
                                        <option>Maintenance</option>
                                        <option>Marketing</option>
                                        <option>Delivery/Gas</option>
                                        <option>Office</option>
                                        <option>Contractors</option>
                                        <option>Software/Fees</option>
                                        <option>Other</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Attach Receipt (Optional)</label>
                                <div className="flex items-center gap-2">
                                    <input 
                                        type="file" 
                                        id="receiptUpload"
                                        accept="image/*,.pdf" 
                                        onChange={e => setReceiptFile(e.target.files[0])} 
                                        className="hidden" 
                                    />
                                    <label htmlFor="receiptUpload" className="cursor-pointer flex-grow text-center bg-gray-50 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-100 transition truncate px-2">
                                        {receiptFile ? `📄 ${receiptFile.name}` : '📁 Choose File'}
                                    </label>
                                    {receiptFile && <button onClick={() => setReceiptFile(null)} className="text-red-500 p-2 hover:bg-red-50 rounded-lg">✕</button>}
                                </div>
                            </div>
                            <button onClick={handleAdd} disabled={isSaving || !newExpense.description || !newExpense.amount} className="w-full bg-pink-600 text-white py-3 rounded-lg font-bold hover:bg-pink-700 transition shadow-md disabled:bg-gray-300 disabled:cursor-not-allowed mt-2">
                                {isSaving ? 'Uploading & Saving...' : 'Add Expense'}
                            </button>
                        </div>
                    </div>

                    {/* Expense Breakdown Visualizer */}
                    <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                        <h3 className="text-lg font-bold mb-4 text-gray-800 border-b pb-2">Category Breakdown</h3>
                        <div className="space-y-4">
                            {Object.keys(expensesByCategory).length > 0 ? (
                                Object.entries(expensesByCategory).sort((a,b) => b[1] - a[1]).map(([cat, amt]) => {
                                    const percentage = totalExpenses > 0 ? (amt / totalExpenses) * 100 : 0;
                                    return (
                                        <div key={cat} className="group">
                                            <div className="flex justify-between text-sm mb-1.5">
                                                <span className="text-gray-600 font-semibold">{cat}</span>
                                                <span className="font-bold text-gray-900">${amt.toFixed(2)} <span className="text-[10px] text-gray-400 font-normal ml-1">({percentage.toFixed(0)}%)</span></span>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden shadow-inner">
                                                <div className="bg-gradient-to-r from-pink-400 to-pink-500 h-full rounded-full transition-all duration-1000 ease-out" style={{ width: `${percentage}%` }}></div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-sm text-gray-500 italic text-center py-4">No expenses recorded for this period.</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Expense List */}
                <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-lg border border-gray-100 flex flex-col h-full max-h-[800px]">
                    <h3 className="text-lg font-bold mb-4 text-gray-800 border-b pb-2">Expense Ledger</h3>
                    <div className="overflow-y-auto flex-grow pr-2 custom-scrollbar">
                        {filteredExpenses.length > 0 ? (
                            <table className="w-full text-sm text-left border-collapse">
                                <thead className="bg-gray-50 text-gray-500 sticky top-0 z-10 shadow-sm">
                                    <tr>
                                        <th className="p-3 font-semibold rounded-tl-md">Date</th>
                                        <th className="p-3 font-semibold">Description</th>
                                        <th className="p-3 font-semibold">Category</th>
                                        <th className="p-3 font-semibold text-center">Receipt</th>
                                        <th className="p-3 font-semibold text-right">Amount</th>
                                        <th className="p-3 rounded-tr-md"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredExpenses.map(e => (
                                        <tr key={e.id} className="hover:bg-gray-50 transition group">
                                            <td className="p-3 text-gray-600 whitespace-nowrap">{new Date(e.date).toLocaleDateString()}</td>
                                            <td className="p-3 font-medium text-gray-900">{e.description}</td>
                                            <td className="p-3"><span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold">{e.category}</span></td>
                                            <td className="p-3 text-center">
                                                {e.receiptUrl ? (
                                                    <a href={e.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 bg-blue-50 p-1.5 rounded inline-block transition" title="View Receipt">
                                                        📄
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-300" title="No receipt attached">-</span>
                                                )}
                                            </td>
                                            <td className="p-3 text-right font-bold text-red-600">-${e.amount.toFixed(2)}</td>
                                            <td className="p-3 text-right opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button onClick={() => handleDelete(e.id)} className="text-gray-400 hover:text-red-500 transition font-bold p-1 rounded-full hover:bg-red-50" title="Delete record">✕</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-16 text-gray-400 border-2 border-dashed border-gray-100 rounded-xl bg-gray-50/50 mt-4">
                                <div className="text-4xl mb-3 opacity-50">🧾</div>
                                <p className="font-semibold text-gray-600">No expenses found.</p>
                                <p className="text-sm mt-1">Add your costs (inventory, gas, marketing) to calculate your true net profit.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function VendorPayouts({ user, functions, isUserAuthenticated, bookings }) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const transactionHistory = useMemo(() => {
        if (!bookings) return [];
        const payments = [];
        bookings.forEach(booking => {
            if (booking.payments && Array.isArray(booking.payments)) {
                booking.payments.forEach(payment => {
                    payments.push({
                        id: payment.stripePaymentId || `${booking.id}_${payment.date?.seconds || Date.now()}`,
                        amount: payment.amount,
                        date: payment.date?.toDate ? payment.date.toDate() : (payment.date?.seconds ? new Date(payment.date.seconds * 1000) : new Date()),
                        type: payment.type,
                        bookingTitle: booking.listingTitle,
                        bookingId: booking.id,
                        renterName: booking.renterName
                    });
                });
            }
        });
        return payments.sort((a, b) => b.date - a.date);
    }, [bookings]);

    const handleOnboarding = async () => {
        setIsLoading(true);
        setError('');

        if (!functions) {
            setError("Configuration Error: Functions not initialized. Refresh and try again.");
            setIsLoading(false);
            return;
        }

        try {
            const createStripeAccount = httpsCallable(functions, 'createStripeAccount');
            let origin = window.location.origin;
            if (!origin || origin === 'null' || origin.includes('about:') || origin.includes('file:')) {
                origin = 'https://hostitdotcom.firebaseapp.com';
            }

            const result = await createStripeAccount({ 
                origin: origin,
                returnUrl: `${origin}/#dashboard`,
                refreshUrl: `${origin}/#dashboard`,
                email: user.email,
                uid: user.uid
            });

            if (result.data && result.data.url) {
                window.location.href = result.data.url;
            } else {
                throw new Error("Server returned an invalid response (no redirect URL).");
            }
        } catch (err) {
            console.error("Stripe onboarding failed:", err);
            const msg = err.message && err.message.includes('internal')
                ? "Connection failed (Internal Error). Please check backend configuration (Stripe Secrets) and logs."
                : (err.message || "Failed to start Stripe onboarding.");
            setError(msg);
            setIsLoading(false);
        }
    };

    if (!user || !isUserAuthenticated) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-lg">
                <p>Please log in to manage payouts.</p>
            </div>
        );
    }
    
    if (user.isVendor && !user.stripeAccountId) {
        return (
             <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-xl font-bold mb-4">Set Up Payouts</h3>
                <p className="text-gray-600 mb-4">
                    To receive payments for your rentals, you need to set up a Standard account with Stripe. Click the button below to get started. You will be redirected to Stripe to securely enter your information.
                </p>
                <button onClick={handleOnboarding} disabled={isLoading} className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400">
                    {isLoading ? 'Redirecting...' : 'Connect with Stripe'}
                </button>
                 {error && <p className="text-red-500 mt-2">{error}</p>}
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-xl font-bold mb-4">Payouts Dashboard</h3>
                <p className="text-gray-600 mb-4">
                Your account is connected to Stripe. You can view your balance, manage payouts, and configure your bank details directly on your Stripe Standard dashboard.
                </p>
                <a href="https://dashboard.stripe.com/" target="_blank" rel="noopener noreferrer" className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition">
                    Go to Stripe Dashboard
                </a>
                {error && <p className="text-red-500 mt-2">{error}</p>}
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-100">
                <h3 className="text-xl font-bold mb-4 text-gray-800">Recent Transactions</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-500 border-b">
                            <tr>
                                <th className="p-3 font-semibold">Date</th>
                                <th className="p-3 font-semibold">Booking Details</th>
                                <th className="p-3 font-semibold">Type</th>
                                <th className="p-3 font-semibold text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {transactionHistory.length > 0 ? (
                                transactionHistory.map((tx) => (
                                    <tr key={tx.id} className="hover:bg-gray-50 transition">
                                        <td className="p-3 text-gray-600 whitespace-nowrap">
                                            {tx.date.toLocaleDateString()}
                                            <span className="block text-xs text-gray-400">{tx.date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                        </td>
                                        <td className="p-3">
                                            <p className="font-medium text-gray-900 truncate max-w-[200px]">{tx.bookingTitle}</p>
                                            <p className="text-xs text-gray-500">{tx.renterName}</p>
                                        </td>
                                        <td className="p-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold capitalize ${tx.type === 'final' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                                {tx.type === 'initial' ? 'Deposit' : tx.type}
                                            </span>
                                        </td>
                                        <td className="p-3 text-right font-bold text-green-600">
                                            +${tx.amount.toFixed(2)}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="p-8 text-center text-gray-500 italic">
                                        No transactions recorded yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}